import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";

interface BookingDetails {
  id: string;
  name: string;
  mobile: string;
  startTime: string;
}

interface ConfirmModalProps {
  onClose: () => void;
  bookingDetails: BookingDetails;
  arenaPhone?: string;
  whatsAppOptIn?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  onClose,
  bookingDetails,
  arenaPhone,
  whatsAppOptIn,
}) => {
  const [copied, setCopied] = useState(false);
  const date = new Date(bookingDetails.startTime);

  const formatTime = (date: Date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? "0" + minutes : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    const monthName = monthNames[parseInt(month, 10) - 1];
    return `${day} ${monthName} ${year}`;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(bookingDetails.id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="fixed inset-0 flex justify-center items-end md:items-center z-50 px-4 pb-4 md:pb-0"
      style={{ background: "rgba(0,0,0,0.75)", overflowY: "auto" }}
    >
      <div
        className="w-full max-w-sm rounded-3xl p-6 relative my-4"
        style={{ background: "#131820", border: "1px solid rgba(255,255,255,0.04)", borderTop: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 4px 8px rgba(0,0,0,0.55), 0 12px 36px rgba(0,0,0,0.50), 0 24px 64px rgba(0,0,0,0.35)" }}
      >
        {/* Success animation */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative flex items-center justify-center mb-4" style={{ width: 80, height: 80 }}>
            <div
              className="success-ripple absolute rounded-full"
              style={{ width: 80, height: 80, border: "2px solid #10b981", opacity: 0.6 }}
            />
            <div
              className="success-ripple absolute rounded-full"
              style={{ width: 80, height: 80, border: "2px solid #10b981", opacity: 0.4 }}
            />
            <div
              className="relative flex items-center justify-center rounded-full"
              style={{
                width: 64, height: 64,
                background: "linear-gradient(135deg, #1a4d2e 0%, #2d7a4e 100%)",
                animation: "scaleIn 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M8 16l5.5 5.5L24 10"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="checkmark-path"
                />
              </svg>
            </div>
          </div>

          <h2
            className="text-2xl font-bold text-white text-center mb-1"
            style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", letterSpacing: "0.02em" }}
          >
            Booking Confirmed!
          </h2>
          <p style={{ color: "#a0aec0", fontSize: 14 }}>Your bay is reserved</p>
        </div>

        {/* QR Code for check-in */}
        <div
          className="flex flex-col items-center rounded-2xl p-4 mb-4"
          style={{ background: "#ffffff" }}
        >
          <QRCodeSVG
            value={bookingDetails.id}
            size={140}
            bgColor="#ffffff"
            fgColor="#0f1419"
            level="M"
          />
          <p style={{ color: "#6b7280", fontSize: 11, marginTop: 8, textAlign: "center" }}>
            Show this QR at the arena for check-in
          </p>
        </div>

        {/* Booking ID card */}
        <div
          className="rounded-2xl p-4 mb-4"
          style={{ background: "#252d3a", boxShadow: "0 2px 8px rgba(0,0,0,0.45)" }}
        >
          <p style={{ color: "#6b7280", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
            Booking ID
          </p>
          <div className="flex items-center justify-between gap-2">
            <span
              className="text-white font-bold text-lg flex-1 truncate"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              {bookingDetails.id}
            </span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: copied ? "#10b981" : "#2d7a4e",
                color: "#ffffff",
                flexShrink: 0,
              }}
            >
              {copied ? (
                <>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l2.5 2.5L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <rect x="1" y="3" width="7" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M4 3V2a1 1 0 011-1h5a1 1 0 011 1v7a1 1 0 01-1 1H9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Detail rows */}
        <div
          className="rounded-2xl p-4 mb-4 space-y-3"
          style={{ background: "#252d3a", boxShadow: "0 2px 8px rgba(0,0,0,0.45)" }}
        >
          <div className="flex justify-between items-center">
            <span style={{ color: "#6b7280", fontSize: 13 }}>Name</span>
            <span className="text-white font-medium text-sm">{bookingDetails.name.toUpperCase()}</span>
          </div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
          <div className="flex justify-between items-center">
            <span style={{ color: "#6b7280", fontSize: 13 }}>Mobile</span>
            <span className="text-white font-medium text-sm" style={{ fontFamily: "'Courier New', monospace" }}>
              {bookingDetails.mobile}
            </span>
          </div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
          <div className="flex justify-between items-center">
            <span style={{ color: "#6b7280", fontSize: 13 }}>Date</span>
            <span className="font-semibold text-sm" style={{ color: "#FF5A22" }}>{formatDate(date)}</span>
          </div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
          <div className="flex justify-between items-center">
            <span style={{ color: "#6b7280", fontSize: 13 }}>Start Time</span>
            <span className="font-semibold text-sm" style={{ color: "#FF5A22", fontFamily: "'Courier New', monospace" }}>
              {formatTime(date)}
            </span>
          </div>
          {arenaPhone && (
            <>
              <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />
              <div className="flex justify-between items-center">
                <span style={{ color: "#6b7280", fontSize: 13 }}>Arena Contact</span>
                <span className="font-medium text-sm text-white" style={{ fontFamily: "'Courier New', monospace" }}>
                  {arenaPhone}
                </span>
              </div>
            </>
          )}
        </div>

        {/* WhatsApp reminder confirmation */}
        {whatsAppOptIn && (
          <div
            className="flex items-center gap-3 rounded-2xl px-4 py-3 mb-4"
            style={{ background: "rgba(37,211,102,0.1)", border: "1.5px solid rgba(37,211,102,0.25)" }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366" style={{ flexShrink: 0 }}>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span style={{ color: "#25D366", fontSize: 13, fontWeight: 600 }}>
              You'll get a WhatsApp reminder before your session
            </span>
          </div>
        )}

        {/* Note */}
        <p className="text-center text-xs mb-5" style={{ color: "#f59e0b" }}>
          Up to 5 persons allowed per bay
        </p>

        {/* Done button */}
        <button
          onClick={onClose}
          className="w-full py-3 rounded-2xl font-bold text-sm transition-all"
          style={{
            background: "linear-gradient(135deg, #FF5A22 0%, #FF8B5E 100%)",
            color: "#0f1419",
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default ConfirmModal;
